import { useState, useEffect } from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Minimize2, Upload } from "lucide-react";
import { Progress } from "@/components/ui/progress";

interface UploadProgressDialogProps {
  open: boolean;
  uploadStatus: {
    video: boolean;
    audio: boolean;
  };
  progress?: number; // 0-100
  onMinimize?: () => void;
  onCancel?: () => void;
  onRetry?: () => void;
  onForceSubmit?: () => void;
}

export function UploadProgressDialog({ open, uploadStatus, progress, onMinimize, onCancel, onRetry, onForceSubmit }: UploadProgressDialogProps) {
  const [waitingTime, setWaitingTime] = useState(0);
  const [showDismissWarning, setShowDismissWarning] = useState(false);

  // Track waiting time
  useEffect(() => {
    if (!open) {
      setWaitingTime(0);
      setShowDismissWarning(false);
      return;
    }

    const interval = setInterval(() => {
      setWaitingTime(prev => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [open]);

  const isAtFinalizing = progress === 100;

  const getUploadMessage = () => {
    if (isAtFinalizing) {
      return "Finalizing upload...";
    }
    if (uploadStatus.video && uploadStatus.audio) {
      return "Uploading video and audio...";
    }
    if (uploadStatus.video) {
      return "Uploading video...";
    }
    if (uploadStatus.audio) {
      return "Uploading audio...";
    }
    return "Processing...";
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <AlertDialog open={open}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <div className="flex items-center justify-between">
            <AlertDialogTitle className="flex items-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              Upload in Progress
            </AlertDialogTitle>
            {onMinimize && (
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={onMinimize}
                className="h-8 w-8"
              >
                <Minimize2 className="h-4 w-4" />
              </Button>
            )}
          </div>
          <AlertDialogDescription className="space-y-4">
            <p className="text-base font-medium text-foreground">
              {getUploadMessage()}
            </p>
            <Progress value={progress ?? 0} className="w-full" />
            <p className="text-sm font-medium text-foreground">
              {progress !== undefined ? `${Math.round(progress)}% complete` : 'Starting upload...'}
            </p>
            <p className="text-sm text-muted-foreground">
              Please wait while we upload your response. Do not close this window or navigate away.
            </p>
            
            {/* Show waiting time after 30 seconds, or 10 seconds if at 100% */}
            {(waitingTime >= 30 || (isAtFinalizing && waitingTime >= 10)) && (
              <p className="text-xs text-muted-foreground">
                Waiting: {formatTime(waitingTime)}
              </p>
            )}

            {/* Show minimize option after 60 seconds */}
            {waitingTime >= 60 && onMinimize && !showDismissWarning && (
              <div className="pt-2 border-t border-border">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={onMinimize}
                  className="w-full"
                >
                  <Minimize2 className="h-4 w-4 mr-2" />
                  Minimize and continue in background
                </Button>
              </div>
            )}

            {/* Show Retry and Cancel options after 60 seconds */}
            {waitingTime >= 60 && (onRetry || onCancel) && (
              <div className="pt-2 border-t border-border space-y-2">
                <p className="text-xs text-muted-foreground">
                  Upload is taking longer than expected.
                </p>
                {onRetry && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={onRetry}
                    className="w-full"
                  >
                    Retry Upload
                  </Button>
                )}
                {onCancel && (
                  <Button 
                    variant="destructive" 
                    size="sm" 
                    onClick={onCancel}
                    className="w-full"
                  >
                    Cancel & Re-record
                  </Button>
                )}
              </div>
            )}

            {/* Show Force Submit after 90 seconds when stuck at 100% */}
            {isAtFinalizing && waitingTime >= 90 && onForceSubmit && (
              <div className="pt-2 border-t border-border space-y-2">
                <p className="text-xs text-muted-foreground">
                  Upload reached 100% but confirmation is delayed. Your response is likely saved.
                </p>
                <Button 
                  variant="default" 
                  size="sm" 
                  onClick={onForceSubmit}
                  className="w-full"
                >
                  Force Submit & Continue
                </Button>
              </div>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// Minimized upload indicator component
export function MinimizedUploadIndicator({ 
  progress, 
  onClick 
}: { 
  progress?: number; 
  onClick: () => void;
}) {
  return (
    <div 
      className="fixed bottom-4 right-4 z-50 bg-primary text-primary-foreground rounded-full px-4 py-2 shadow-lg cursor-pointer hover:bg-primary/90 transition-colors flex items-center gap-2"
      onClick={onClick}
    >
      <Upload className="h-4 w-4 animate-pulse" />
      <span className="text-sm font-medium">
        Uploading {progress !== undefined ? `${Math.round(progress)}%` : '...'}
      </span>
    </div>
  );
}
